import type { CounterpartyRefKind, CounterpartySnapshot } from '../counterparties/index.js';
import type { CoverShipmentStatus } from '../cover-shipments/index.js';
import type { ShipmentFollowUpComparisonStatus } from '../shipments/list-scale-tickets.dto.js';
import type { ShipmentStatus } from '../shipments/shipments-types.js';

export const outboundRowTypeValues = ['regular', 'cover'] as const;
export type OutboundRowType = (typeof outboundRowTypeValues)[number];

export const outboundCurrentStepValues = [
  'draft',
  'ready_to_depart',
  'departed',
  'voided',
] as const;
export type OutboundCurrentStep = (typeof outboundCurrentStepValues)[number];

export interface ListOutboundRequest {
  companyId: string;
  types?: OutboundRowType[];
  materialId?: string;
  currentSteps?: OutboundCurrentStep[];
  createdFrom?: string;
  createdTo?: string;
  needsScaleTicket?: boolean;
  needsFollowUp?: boolean;
}

export interface OutboundDateSummary {
  reportPeriodStartAt: string;
  reportPeriodEndAt: string;
  createdAt: string;
  departedAt: string | null;
  voidedAt: string | null;
  sortAt: string;
}

export interface OutboundWeightSummary {
  documentTotalWeightLb: number | null;
  huSelectedPhysicalWeightLb: number | null;
  processorReceivedWeightLb: number | null;
}

export interface OutboundStatusSummary {
  sourceStatus: ShipmentStatus | CoverShipmentStatus;
  currentStep: OutboundCurrentStep;
}

export interface OutboundScaleTicketSummary {
  internalTicketCount: number;
  processorTicketCount: number;
  latestProcessorWeighedAt: string | null;
  hasCompleteProcessorTicket: boolean;
  followUpComparisonStatus: ShipmentFollowUpComparisonStatus | null;
  needsScaleTicket: boolean;
  needsFollowUp: boolean;
}

export interface OutboundRelationshipSourceSummary {
  receiptId: string;
  reportNumber: string | null;
  receivedWeightLb: number | null;
  receivedAt: string | null;
  sourceDisplayName: string | null;
  sourceKind: CounterpartyRefKind | null;
}

export interface OutboundDownstreamInboundReceiptSummary {
  receiptId: string;
  reportNumber: string | null;
  receivedWeightLb: number | null;
  confirmedAt: string | null;
}

export interface OutboundDownstreamCoverSummary {
  coverShipmentId: string;
  reportNumber: string | null;
  status: CoverShipmentStatus;
}

export interface OutboundRelationshipSummary {
  coverAttachmentCount: number;
  inboundSourceReportNumbers: string[];
  sourceSummaries: OutboundRelationshipSourceSummary[];
  downstreamInboundReceipt: OutboundDownstreamInboundReceiptSummary | null;
  downstreamCover: OutboundDownstreamCoverSummary | null;
}

export interface OutboundRowBase {
  id: string;
  type: OutboundRowType;
  companyId: string;
  stationId: string;
  materialId: string;
  materialDisplayName: string | null;
  receiver: CounterpartySnapshot;
  receiverDisplayName: string;
  reportNumber: string | null;
  dates: OutboundDateSummary;
  status: OutboundStatusSummary;
  weights: OutboundWeightSummary;
  scaleTicket: OutboundScaleTicketSummary;
  relationship: OutboundRelationshipSummary;
}

export interface RegularOutboundRow extends OutboundRowBase {
  type: 'regular';
  shipmentId: string;
}

export interface CoverOutboundRow extends OutboundRowBase {
  type: 'cover';
  coverShipmentId: string;
}

export type OutboundListRow = RegularOutboundRow | CoverOutboundRow;

export interface OutboundListSummary {
  totalCount: number;
  regularCount: number;
  coverCount: number;
  needsScaleTicketCount: number;
  needsFollowUpCount: number;
}

export interface ListOutboundResponse {
  items: OutboundListRow[];
  summary: OutboundListSummary;
}
